/**
 * NotificationToasts — organism (CES standard)
 * Renders the notificationStore as toast cards fixed to the bottom-right
 * corner of the shell. Success/info toasts auto-dismiss; error toasts stay
 * until the user closes them (they usually require an action).
 */

import { useEffect } from 'react';
import { X } from 'lucide-react';
import {
  useNotificationStore,
  type AppNotification,
} from '../../stores/notificationStore';

export const TOAST_AUTO_DISMISS_MS = 6_000;

const TYPE_CLASSES: Record<AppNotification['type'], string> = {
  success: 'border-green-300 bg-green-50 text-green-800',
  error: 'border-red-300 bg-red-50 text-red-800',
  info: 'border-blue-300 bg-blue-50 text-blue-800',
};

function Toast({ notification }: { notification: AppNotification }) {
  const dismiss = useNotificationStore((state) => state.dismiss);

  // Errors persist (the user may need to act on them); the rest fade out.
  useEffect(() => {
    if (notification.type === 'error') return;
    const timer = setTimeout(() => dismiss(notification.id), TOAST_AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [notification.id, notification.type, dismiss]);

  return (
    <div
      role={notification.type === 'error' ? 'alert' : 'status'}
      data-testid={`toast-${notification.type}`}
      className={`pointer-events-auto flex w-80 items-start gap-2 rounded-lg border px-4 py-3 text-sm shadow-lg ${TYPE_CLASSES[notification.type]}`}
    >
      <p className="flex-1 break-words">{notification.message}</p>
      <button
        type="button"
        aria-label="Dismiss notification"
        data-testid={`toast-dismiss-${notification.id}`}
        className="shrink-0 rounded p-0.5 opacity-60 transition-opacity hover:opacity-100"
        onClick={() => dismiss(notification.id)}
      >
        <X size={16} />
      </button>
    </div>
  );
}

export function NotificationToasts() {
  const notifications = useNotificationStore((state) => state.notifications);
  if (notifications.length === 0) return null;

  return (
    <div
      aria-label="Notifications"
      className="pointer-events-none fixed bottom-6 right-6 z-50 flex flex-col gap-2"
      data-testid="notification-toasts"
    >
      {notifications.map((notification) => (
        <Toast key={notification.id} notification={notification} />
      ))}
    </div>
  );
}
